import { useMemo } from 'react'

import BeatLoader from 'react-spinners/BeatLoader'
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized'
import styled from 'styled-components'

import { useGetViewLikesUsers } from 'api/post'
import { useGetViewLikesStoryUsers } from 'api/stories/stories'
import { UserNoOptionsCard } from 'components-ref/atoms'
import { NoLikes } from 'components-ref/molecules/post/no-likes/no-likes'
import { useDebouncedValue } from 'helpers/hooks/useDebouncedValue/useDebouncedValue'
import { useViewLikesContext } from 'helpers/hooks/useViewLikesContext/useViewLikesContext'
import { type IUserCard } from 'types/interfaces/IUserCard'

type LikesTargetType = 'post' | 'story'

interface ViewLikesUsersListProps {
    type?: LikesTargetType
}

export const ViewLikesUsersList = ({ type = 'post' }: ViewLikesUsersListProps) => {
    const {
        state: { sortDirection, postId, searchTerm }
    } = useViewLikesContext()

    const debouncedSearchTerm = useDebouncedValue<string>({
        initialValue: searchTerm,
        delay: 400
    })

    const queryResult =
        type === 'story'
            ? useGetViewLikesStoryUsers(postId, sortDirection, debouncedSearchTerm)
            : useGetViewLikesUsers(postId, sortDirection, debouncedSearchTerm)

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isFetching
    } = queryResult

    const users = useMemo<IUserCard[]>(
        () => data?.pages?.flatMap(page => page.data.results) ?? [],
        [data]
    )

    const totalLikes = data?.pages?.[0]?.data?.pagination?.total ?? 0
    const isInitialLoading = isLoading && !data
    const isBackgroundFetching = isFetching && !!data
    const isEmpty = !isInitialLoading && totalLikes === 0

    const rowCount = users.length + (hasNextPage ? 1 : 0)

    const isRowLoaded = ({ index }: { index: number }) => index < users.length

    const loadMoreRows = async (): Promise<void> => {
        if (!hasNextPage || isFetchingNextPage) return
        await fetchNextPage()
    }

    return (
        <StyledContainer>
            {isInitialLoading ? (
                <CenteredLoader>
                    <BeatLoader size={10} />
                </CenteredLoader>
            ) : null}

            {isBackgroundFetching ? <TopRightMiniLoader /> : null}

            {!isInitialLoading && isEmpty ? (
                <EmptyStateContainer>
                    <NoLikes />
                </EmptyStateContainer>
            ) : null}

            {!isInitialLoading && !isEmpty ? (
                <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={rowCount}
                    threshold={3}
                >
                    {({ onRowsRendered, registerChild }) => (
                        <AutoSizer>
                            {({ width, height }) => (
                                <List
                                    ref={list => registerChild(list)}
                                    onRowsRendered={onRowsRendered}
                                    width={width}
                                    height={height}
                                    rowCount={users.length}
                                    rowHeight={70}
                                    style={{ overflowY: 'auto', overflowX: 'hidden' }}
                                    containerStyle={{ overflow: 'visible' }}
                                    overscanRowCount={6}
                                    rowRenderer={({ index, key, style }) => {
                                        const item = users[index]
                                        if (!item) return null

                                        return (
                                            <UserNoOptionsCard
                                                item={item}
                                                key={key}
                                                style={style}
                                            />
                                        )
                                    }}
                                />
                            )}
                        </AutoSizer>
                    )}
                </InfiniteLoader>
            ) : null}
        </StyledContainer>
    )
}

const StyledContainer = styled.div`
    margin-top: 20px;
    height: 32rem;
    max-height: 32rem;
    position: relative;
`

const CenteredLoader = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
    background: rgba(255, 255, 255, 0.6);
`

const EmptyStateContainer = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

const TopRightMiniLoader = styled.div`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    border: 2px solid rgba(0, 0, 0, 0.15);
    border-top-color: rgba(0, 0, 0, 0.55);
    animation: spin 0.8s linear infinite;
    z-index: 6;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`